import { Injectable, Inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { APP_CONFIG } from '../../../tokens/app-config.token';
import { Environment } from '../../../../environments/environment.interface';
import { Store } from '@ngrx/store';
import { AuthState, getValidAuthToken } from '../../ngrx/state/auth.state';
import { first, take, tap } from 'rxjs/operators';
import { LogoutAction, SetAuthToken } from '../../ngrx/action/auth.action';
import { getAuthenticationConfig } from '../../guard/authentication.config';
import { CookieService } from 'ngx-cookie-service';
import {
  CustomErrorDefaultMessage,
  ErrorCode,
  StatusCodes,
} from '../../constants/errorCodes';
import { VerifyPhoneDialogComponent } from '../../../common/component/dialogs/verify-phone-dialog/verify-phone-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import _some from 'lodash-es/some';
import { CustomError, CustomErrorAction, CustomErrorCode } from '@dn/libs/ui/dn-common';
import { NotificationService } from '../notifications/notification.service';
import { StickyTypes } from '../notifications/notifications.Interface';
import { _ } from '@angular/cdk/number-property.d-CJVxXUcb';

@Injectable({
  providedIn: 'root',
})
export class BackendInterceptorService implements HttpInterceptor {
  currentToken: string;
  private readonly authConfig: ReturnType<typeof getAuthenticationConfig>;

  constructor(
    private readonly authStore: Store<AuthState>,
    private readonly cookieSerivce: CookieService,
    private readonly dialog: MatDialog,
    private readonly noty: NotificationService,
    @Inject(APP_CONFIG) private readonly appConfig: Environment,
  ) {
    this.authConfig = getAuthenticationConfig(this.appConfig);
    this.authStore
      .select(getValidAuthToken)
      .pipe(
        take(1),
        tap((token) => (this.currentToken = token)),
      )
      .subscribe();
  }

  /**
   * Intercept the request sent using HttpClient
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const promise = this.handleRequest(request, next);
    return this.convertPromiseToObservable(promise);
  }

  private async handleRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Promise<HttpEvent<any>> {
    if (!this.isBackendRequest(request)) {
      return next.handle(request).toPromise();
    }
    const authToken = await this.getAuthTokenFromStore();
    if (!authToken) {
      this.authStore.dispatch(LogoutAction());
      return of(null).toPromise();
    }

    return this.buildBackendRequestPayload(request)
      .then((backendRequestPayload) =>
        next.handle(backendRequestPayload).pipe().toPromise(),
      )
      .then((resp: HttpResponse<any>) => {
        this.checkCookieChange();
        return resp;
      })
      .catch((err: HttpErrorResponse) => this.handleError(err));
  }

  private buildHeadersWithAuthToken(token: string): HttpHeaders {
    const authorizationValue = this.buildHeaderAuthorizationValue(token);
    const headers = this.buildHeaderWithAutorizationValue(authorizationValue);
    return headers;
  }

  private buildHeaderWithAutorizationValue(
    authorizationValue: string,
  ): HttpHeaders {
    return new HttpHeaders({
      Authorization: authorizationValue,
    });
  }

  private buildHeaderAuthorizationValue(token: string): string {
    let authorizationValue = '';
    if (token) {
      authorizationValue = `Bearer ${token}`;
    }
    return authorizationValue;
  }

  private async buildBackendRequestPayload(
    request: HttpRequest<any>,
  ): Promise<HttpRequest<any>> {
    const authToken = await this.getAuthTokenFromStore();

    let headers = this.buildHeadersWithAuthToken(authToken);

    if (this.appConfig.name === 'dev') {
      headers = headers.append('team-id', this.appConfig.teamId);
      headers = headers.append('user-id', this.appConfig.userId);
      headers = headers.append('email', this.appConfig.email);
    }

    const transformedRequest = request.clone({
      headers,
      body: request.body,
      withCredentials: true,
    });

    return transformedRequest;
  }

  private async getAuthTokenFromStore(): Promise<string> {
    return this.authStore.select(getValidAuthToken).pipe(first()).toPromise();
  }

  private convertPromiseToObservable(
    promise: Promise<HttpEvent<any>>,
  ): Observable<HttpEvent<any>> {
    return from(promise);
  }

  /**
   * Is the request to micro services backend
   */
  private isBackendRequest(request: HttpRequest<any>): boolean {
    return request.url.startsWith(this.appConfig.BACKEND);
  }

  /**
   * Handle error by status
   *  By default it will throw the error to the request initiator
   * */
  private handleError(event: HttpErrorResponse): Promise<any> | void {
    const { errorCode } = event.error;

    if (errorCode === ErrorCode.NOT_TRUSTED_AND_NOT_PHONE_VERIFIED) {
      return this.handleNotTrustedUserError();
    }

    switch (event.status) {
      case StatusCodes.UnAuthorized:
        return this.handleUnAuthorized(event);
      case StatusCodes.Service_Unavailable:
        return this.handleUnAvailable(event);
      default:
        return throwError(event).toPromise();
    }
  }

  private handleUnAvailable(event) {
    const { error } = event || {};
    const { errorCode } = error as CustomError || {};
    switch (errorCode) {
      case CustomErrorCode.CUSTOM_ERROR:
        return this.handleAction(event);
      default:
        break;
    }
  }

  private handleAction(event: any) {
    const { error } = event || {};
    const { action, message } = error || {};
    switch (action) {
      case CustomErrorAction.SHOW_NOTI:
        return this.CustomActionShowNoti(message);

      default:
        break;
    }
  }

  private CustomActionShowNoti(errorMessage = CustomErrorDefaultMessage) {
    this.noty.showStickyNotification.next({
      type: StickyTypes.ERROR,
      componentData: {
        body: errorMessage,
        showCta: false,
        ctaText: null,
        ctaClick: null,
      },
    });
  }

  private handleNotTrustedUserError() {
    if (this.isVerifyPhoneDialogOpen()) return;

    this.dialog
      .open(VerifyPhoneDialogComponent, { disableClose: true })
      .afterClosed()
      .subscribe((res) => {
        if (!res) return;
        this.authStore.dispatch(LogoutAction());
      });
  }

  private isVerifyPhoneDialogOpen(): boolean {
    return _some(
      this.dialog.openDialogs,
      (dialog: MatDialogRef<any, any>) =>
        dialog.componentInstance instanceof VerifyPhoneDialogComponent,
    );
  }
  /**
   * Handle error by errorCode of status 401
   *  By default it will sign-out the user
   * */
  private handleUnAuthorized(
    event: HttpErrorResponse,
  ): Promise<HttpEvent<any>> | void {
    this.authStore.dispatch(LogoutAction());
  }

  private checkCookieChange(): void {
    const currentTokenInCookies = this.cookieSerivce.get(this.authConfig.authTokenKeyName);
    if (currentTokenInCookies && currentTokenInCookies !== this.currentToken) {
      this.authStore.dispatch(
        SetAuthToken({ authToken: currentTokenInCookies }),
      );
    }
  }
}
